import PropTypes from 'prop-types'
import classNames from 'classnames'
import styles from './styles/Icon.css'

/**
 * Icon component using inline SVGs
 *
 * All icons are defined inline for:
 * - No extra network requests
 * - Full CSS color control via currentColor
 * - Easy updates from Figma (just update the paths)
 *
 * Usage:
 *   <Icon name="chat" />
 *   <Icon name="table" size={20} />
 *   <ChatIcon size={16} color="#333" />
 *
 * To update an icon from Figma:
 *   1. Copy the SVG from Figma
 *   2. Extract the path data and viewBox
 *   3. Update the corresponding entry in ICONS below
 */

// All icons defined as inline SVG data
// Format: { viewBox, paths[], filled?, type?, circles? }
const ICONS = {
  // ============================================
  // CHAT & MESSAGING
  // ============================================

  // Chat bubble icon with dots - for assistant/conversations (from Figma)
  chat: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: [
      'M12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 13.529 3.12096 14.9713 3.77778 16.2418L2.75 21.25L7.88889 20.2885C9.12732 20.9039 10.5232 21.25 12 21.25Z'
    ],
    // Filled dots inside the chat bubble
    filledPaths: [
      'M7.5 11C7.91421 11 8.25 11.3358 8.25 11.75C8.25 12.1642 7.91421 12.5 7.5 12.5C7.08579 12.5 6.75 12.1642 6.75 11.75C6.75 11.3358 7.08579 11 7.5 11ZM12 11C12.4142 11 12.75 11.3358 12.75 11.75C12.75 12.1642 12.4142 12.5 12 12.5C11.5858 12.5 11.25 12.1642 11.25 11.75C11.25 11.3358 11.5858 11 12 11ZM16.5 11C16.9142 11 17.25 11.3358 17.25 11.75C17.25 12.1642 16.9142 12.5 16.5 12.5C16.0858 12.5 15.75 12.1642 15.75 11.75C15.75 11.3358 16.0858 11 16.5 11Z'
    ]
  },

  // New chat icon - chat bubble with pencil (filled from Figma)
  newChat: {
    viewBox: '0 0 24 24',
    filled: true,
    paths: [
      'M3 18.6496V5.34978C3 5.08222 2.99991 4.83633 3.0166 4.63201C3.03402 4.41892 3.07312 4.18549 3.19043 3.95525C3.35816 3.62611 3.62596 3.35837 3.95508 3.1906C4.18529 3.0733 4.41877 3.03322 4.63184 3.0158C4.83611 2.99911 5.08211 3.00017 5.34961 3.00017H11.25C11.6642 3.00017 12 3.33596 12 3.75017C11.9999 4.16432 11.6642 4.50017 11.25 4.50017H5.34961C5.05744 4.50017 4.88298 4.50037 4.75391 4.51092C4.63386 4.52075 4.62034 4.53586 4.63672 4.52752C4.58968 4.55149 4.55131 4.58985 4.52734 4.63689C4.53569 4.62051 4.52058 4.63388 4.51074 4.75408C4.5002 4.88317 4.5 5.05752 4.5 5.34978V18.6496C4.5 18.9416 4.50023 19.1162 4.51074 19.2453C4.52058 19.3657 4.5357 19.3798 4.52734 19.3635C4.55131 19.4105 4.58968 19.4489 4.63672 19.4728C4.62034 19.4645 4.63391 19.4786 4.75391 19.4885C4.88298 19.499 5.05744 19.5002 5.34961 19.5002H18.6504C18.9426 19.5002 19.117 19.499 19.2461 19.4885C19.3661 19.4786 19.3797 19.4645 19.3633 19.4728C19.4103 19.4489 19.4487 19.4105 19.4727 19.3635C19.4643 19.3798 19.4794 19.3657 19.4893 19.2453C19.4998 19.1162 19.5 18.9416 19.5 18.6496V12.7502C19.5 12.336 19.8358 12.0002 20.25 12.0002C20.6642 12.0002 21 12.336 21 12.7502V18.6496C21 18.9172 21.0001 19.164 20.9834 19.3683C20.966 19.5812 20.9267 19.8141 20.8096 20.0441C20.6418 20.3734 20.3741 20.642 20.0449 20.8097C19.8147 20.927 19.5812 20.9661 19.3682 20.9836C19.1639 21.0003 18.9179 21.0002 18.6504 21.0002H5.34961C5.08211 21.0002 4.83611 21.0003 4.63184 20.9836C4.41879 20.9661 4.18527 20.927 3.95508 20.8097C3.62587 20.642 3.35818 20.3733 3.19043 20.0441C3.07334 19.8141 3.03402 19.5812 3.0166 19.3683C2.9999 19.164 3 18.9172 3 18.6496ZM17.5127 2.42693C18.1961 1.74351 19.3039 1.74351 19.9873 2.42693L21.5732 4.01287C22.2565 4.6963 22.2566 5.80411 21.5732 6.48748L16.2803 11.7804C16.1396 11.9211 15.9489 12.0002 15.75 12.0002H12.75C12.3358 12.0002 12.0001 11.6643 12 11.2502V8.25017C12 8.0513 12.0791 7.86054 12.2197 7.7199L17.5127 2.42693ZM18.9268 3.48748C18.8291 3.38985 18.6709 3.38985 18.5732 3.48748L13.5 8.56072V10.5002H15.4395L20.5127 5.42693C20.6103 5.32935 20.6102 5.17106 20.5127 5.07342L18.9268 3.48748Z'
    ]
  },

  // ============================================
  // TABLE & DATA
  // ============================================

  // Table/grid icon - for structured responses (filled icon from Figma)
  table: {
    viewBox: '0 0 24 24',
    filled: true,
    paths: [
      'M19.5 10.5H10.5V19.5H18.6504C18.9426 19.5 19.117 19.4998 19.2461 19.4893C19.3662 19.4794 19.3797 19.4643 19.3633 19.4727C19.4103 19.4487 19.4487 19.4103 19.4727 19.3633C19.4643 19.3797 19.4794 19.3662 19.4893 19.2461C19.4998 19.117 19.5 18.9426 19.5 18.6504V10.5ZM4.5 18.6504C4.5 18.9426 4.5002 19.117 4.51074 19.2461C4.52058 19.3662 4.53569 19.3797 4.52734 19.3633C4.55131 19.4103 4.58968 19.4487 4.63672 19.4727C4.62034 19.4643 4.63384 19.4794 4.75391 19.4893C4.88298 19.4998 5.05744 19.5 5.34961 19.5H9V10.5H4.5V18.6504ZM19.5 5.34961C19.5 5.05744 19.4998 4.88298 19.4893 4.75391C19.4794 4.63384 19.4643 4.62034 19.4727 4.63672C19.4487 4.58968 19.4103 4.55131 19.3633 4.52734C19.3797 4.53569 19.3662 4.52058 19.2461 4.51074C19.117 4.5002 18.9426 4.5 18.6504 4.5H10.5V9H19.5V5.34961ZM4.5 9H9V4.5H5.34961C5.05744 4.5 4.88298 4.5002 4.75391 4.51074C4.63384 4.52058 4.62034 4.53569 4.63672 4.52734C4.58968 4.55131 4.55131 4.58968 4.52734 4.63672C4.53569 4.62034 4.52058 4.63384 4.51074 4.75391C4.5002 4.88298 4.5 5.05744 4.5 5.34961V9ZM21 18.6504C21 18.9179 21.0001 19.1639 20.9834 19.3682C20.966 19.5812 20.9269 19.8147 20.8096 20.0449C20.6418 20.374 20.374 20.6418 20.0449 20.8096C19.8147 20.9269 19.5812 20.966 19.3682 20.9834C19.1639 21.0001 18.9179 21 18.6504 21H5.34961C5.08211 21 4.83611 21.0001 4.63184 20.9834C4.41879 20.966 4.18527 20.9269 3.95508 20.8096C3.62597 20.6418 3.35818 20.374 3.19043 20.0449C3.07314 19.8147 3.03403 19.5812 3.0166 19.3682C2.99991 19.1639 3 18.9179 3 18.6504V5.34961C3 5.08211 2.99991 4.83611 3.0166 4.63184C3.03403 4.41879 3.07314 4.18527 3.19043 3.95508C3.35818 3.62597 3.62597 3.35818 3.95508 3.19043C4.18527 3.07314 4.41879 3.03403 4.63184 3.0166C4.83611 2.99991 5.08211 3 5.34961 3H18.6504C18.9179 3 19.1639 2.99991 19.3682 3.0166C19.5812 3.03403 19.8147 3.07314 20.0449 3.19043C20.374 3.35818 20.6418 3.62597 20.8096 3.95508C20.9269 4.18527 20.966 4.41879 20.9834 4.63184C21.0001 4.83611 21 5.08211 21 5.34961V18.6504Z'
    ]
  },

  // ============================================
  // ACTIONS
  // ============================================

  // Trash/delete
  trash: {
    viewBox: '0 0 24 24',
    paths: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2']
  },

  // Edit/pencil
  edit: {
    viewBox: '0 0 24 24',
    paths: ['M12 20h9', 'M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z']
  },

  // Share/upload
  share: {
    viewBox: '0 0 24 24',
    paths: ['M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8', 'M16 6l-4-4-4 4', 'M12 2v13']
  },

  // Copy
  copy: {
    viewBox: '0 0 24 24',
    paths: [
      'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
      'M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z'
    ]
  },

  // Download
  download: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.8,
    paths: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3']
  },

  // Search
  search: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35']
  },

  // Plus
  plus: {
    viewBox: '0 0 24 24',
    paths: ['M12 5v14', 'M5 12h14']
  },

  // X/close
  x: {
    viewBox: '0 0 24 24',
    paths: ['M18 6L6 18', 'M6 6l12 12']
  },

  // Check
  check: {
    viewBox: '0 0 24 24',
    paths: ['M20 6L9 17l-5-5']
  },

  // External link
  externalLink: {
    viewBox: '0 0 24 24',
    paths: ['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6', 'M15 3h6v6', 'M10 14L21 3']
  },

  // ============================================
  // UI ELEMENTS
  // ============================================

  // Help/question
  help: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: [
      'M9.75 9.25V8.75C9.75 8.19772 10.1977 7.75 10.75 7.75H13.25C13.8023 7.75 14.25 8.19772 14.25 8.75V9.96482C14.25 10.2992 14.0829 10.6114 13.8047 10.7969L12.4453 11.7031C12.1671 11.8886 12 12.2008 12 12.5352V13.25M12 16V15.99M21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12ZM12.25 16C12.25 16.1381 12.1381 16.25 12 16.25C11.8619 16.25 11.75 16.1381 11.75 16C11.75 15.8619 11.8619 15.75 12 15.75C12.1381 15.75 12.25 15.8619 12.25 16Z'
    ]
  },

  // Info
  info: {
    viewBox: '0 0 24 24',
    paths: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 16v-4', 'M12 8h.01']
  },

  // Warning/alert
  warning: {
    viewBox: '0 0 24 24',
    paths: [
      'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
      'M12 9v4',
      'M12 17h.01'
    ]
  },

  // Settings/gear
  settings: {
    viewBox: '0 0 24 24',
    paths: [
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
      'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z'
    ]
  },

  // Sparkles/AI
  sparkles: {
    viewBox: '0 0 24 24',
    paths: [
      'M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z'
    ]
  },

  // ============================================
  // NAVIGATION & CHEVRONS
  // ============================================

  chevronRight: {
    viewBox: '0 0 24 24',
    paths: ['M9 18l6-6-6-6']
  },

  chevronLeft: {
    viewBox: '0 0 24 24',
    paths: ['M15 18l-6-6 6-6']
  },

  chevronDown: {
    viewBox: '0 0 24 24',
    paths: ['M6 9l6 6 6-6']
  },

  chevronUp: {
    viewBox: '0 0 24 24',
    paths: ['M18 15l-6-6-6 6']
  },

  // ============================================
  // SIDEBAR
  // ============================================

  // Sidebar collapsed (panel on left with arrow - click to expand)
  sidebarCollapsed: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: ['M3.75 3.75H20.25V20.25H3.75V3.75Z', 'M8.75 4V12V20']
  },

  // Sidebar expanded (panel on left, no arrow - click to collapse)
  sidebarExpanded: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: ['M15.25 9.75L13 12L15.25 14.25', 'M3.75 3.75H20.25V20.25H3.75V3.75Z', 'M8.25 3.75V20.25']
  },

  // ============================================
  // MENUS
  // ============================================

  // More vertical (three dots) - uses circles, not paths
  moreVertical: {
    viewBox: '0 0 24 24',
    type: 'circles',
    circles: [
      { cx: 12, cy: 5, r: 2 },
      { cx: 12, cy: 12, r: 2 },
      { cx: 12, cy: 19, r: 2 }
    ]
  },

  // More horizontal (three dots) - uses filled circles
  moreHorizontal: {
    viewBox: '0 0 24 24',
    type: 'circles',
    circles: [
      { cx: 5, cy: 12, r: 2 },
      { cx: 12, cy: 12, r: 2 },
      { cx: 19, cy: 12, r: 2 }
    ]
  },

  // ============================================
  // MISC
  // ============================================

  // Bookmark
  bookmark: {
    viewBox: '0 0 24 24',
    paths: ['M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z']
  },

  // History/clock
  history: {
    viewBox: '0 0 24 24',
    paths: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2']
  },

  // Folder
  folder: {
    viewBox: '0 0 24 24',
    paths: ['M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z']
  },

  // Eye (visible)
  eye: {
    viewBox: '0 0 24 24',
    paths: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']
  },

  // Eye off (hidden)
  eyeOff: {
    viewBox: '0 0 24 24',
    paths: [
      'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24',
      'M1 1l22 22'
    ]
  },

  // Tag
  tag: {
    viewBox: '0 0 24 24',
    paths: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01']
  },

  // Bell/notifications
  bell: {
    viewBox: '0 0 20 20',
    strokeWidth: 1.25,
    paths: [
      'M13.3333 14.3753C13.3333 16.2163 11.8409 17.7087 10 17.7087C8.15905 17.7087 6.66667 16.2163 6.66667 14.3753M3.125 14.3753H16.875V13.3337L15.5859 10.8337L15.4137 7.42447C15.2695 4.54934 12.8884 2.29199 10 2.29199C7.11155 2.29199 4.7305 4.54934 4.58626 7.42447L4.41406 10.8337L3.125 13.3337V14.3753Z'
    ]
  },

  // Lightbulb
  lightbulb: {
    viewBox: '0 0 24 24',
    paths: [
      'M9 18h6',
      'M10 22h4',
      'M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14'
    ]
  },

  // Search Checklist
  searchChecklist: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: [
      'M7.54297 9.49777L8.66797 10.2478L10.5396 7.75226M13.808 9.00002H16.308M7.54297 15.4989L8.66797 16.2489L10.5396 13.7534M20.25 11.25V4.75C20.25 4.19772 19.8023 3.75 19.25 3.75H4.75C4.19772 3.75 3.75 4.19772 3.75 4.75V19.25C3.75 19.8023 4.19772 20.25 4.75 20.25H11.25M19.3713 19.364C18.1997 20.5355 16.3003 20.5355 15.1287 19.364C13.9571 18.1924 13.9571 16.2929 15.1287 15.1213C16.3003 13.9497 18.1997 13.9497 19.3713 15.1213C20.5429 16.2929 20.5429 18.1924 19.3713 19.364ZM19.3713 19.364L21.25 21.2426'
    ]
  },

  // Dashboard Layout - stroke-based grid icon
  dashboardLayout: {
    viewBox: '0 0 24 24',
    strokeWidth: 1.5,
    paths: [
      'M4 4h16v16H4V4Z', // Outer rectangle
      'M12 4v16', // Vertical center line
      'M4 10h8', // Horizontal line in top-left
      'M12 14h8' // Horizontal line in bottom-right
    ]
  },

  // Get a Demo - screens with arrow
  demo: {
    viewBox: '0 0 20 20',
    strokeWidth: 1.5,
    paths: [
      'M3.12435 8.54167V3.125H17.7077V12.7083H12.291M14.3743 6.45833V8.95833M14.3743 6.45833H11.8743M14.3743 6.45833L11.666 9.16667M9.37435 11.4583H2.29102V17.7083H9.37435V11.4583Z'
    ]
  },

  // MCP Server - cursor arrow with window
  mcp: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M10.1973 9.2373L10.3174 9.26172L17.833 11.3291C18.5578 11.5284 18.6942 12.4967 18.0527 12.8887L14.8477 14.8477L12.8887 18.0527C12.4967 18.6942 11.5284 18.5578 11.3291 17.833L9.26172 10.3174C9.09627 9.71574 9.60439 9.15937 10.1973 9.2373ZM12.3105 16.5039L13.7939 14.0791L13.8525 13.9951C13.9154 13.9155 13.9921 13.8471 14.0791 13.7939L16.5039 12.3105L10.7207 10.7207L12.3105 16.5039ZM16.2246 8.95801V3.95801C16.2244 3.85701 16.143 3.77557 16.042 3.77539H3.95801C3.85701 3.77557 3.77557 3.85701 3.77539 3.95801V16.042C3.77557 16.143 3.85701 16.2244 3.95801 16.2246H8.95801C9.31699 16.2246 9.6084 16.516 9.6084 16.875C9.6084 17.234 9.31699 17.5254 8.95801 17.5254H3.95801C3.13904 17.5252 2.47479 16.861 2.47461 16.042V3.95801C2.47479 3.13904 3.13904 2.47479 3.95801 2.47461H16.042C16.861 2.47479 17.5252 3.13904 17.5254 3.95801V8.95801C17.5254 9.31699 17.234 9.6084 16.875 9.6084C16.516 9.6084 16.2246 9.31699 16.2246 8.95801Z'
    ]
  },

  // Scite API - concentric circles with connection lines
  api: {
    viewBox: '0 0 20 20',
    strokeWidth: 1.5,
    paths: [
      'M3.12565 10C3.12565 13.797 6.20369 16.875 10.0007 16.875C12.9156 16.875 15.4069 15.0608 16.407 12.5M3.12565 10C3.12565 6.20304 6.20369 3.125 10.0007 3.125C12.9156 3.125 15.4069 4.93915 16.407 7.5M3.12565 10H1.45898M14.3757 10C14.3757 12.4162 12.4169 14.375 10.0007 14.375C7.58441 14.375 5.62565 12.4162 5.62565 10C5.62565 7.58375 7.58441 5.625 10.0007 5.625C12.4169 5.625 14.3757 7.58375 14.3757 10ZM14.3757 10H18.5423'
    ]
  },

  // Browser Extension - puzzle piece
  browserExtension: {
    viewBox: '0 0 20 20',
    strokeWidth: 1.5,
    paths: [
      'M3.125 3.95899H7.70833V3.75065C7.70833 2.485 8.73435 1.45898 10 1.45898C11.2657 1.45898 12.2917 2.485 12.2917 3.75065V3.95899H16.875V7.70899H16.6667C15.401 7.70899 14.375 8.735 14.375 10.0007C14.375 11.2663 15.401 12.2923 16.6667 12.2923H16.875V16.0423H3.125V3.95899Z'
    ]
  },

  // Funders - dollar sign in circle
  funders: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M17.0576 9.99902C17.0574 6.10096 13.8971 2.94141 9.99902 2.94141C6.10107 2.94158 2.94158 6.10107 2.94141 9.99902C2.94141 13.8971 6.10096 17.0574 9.99902 17.0576C13.8972 17.0576 17.0576 13.8972 17.0576 9.99902ZM9.34961 14.71V14.0098C8.63852 13.867 8.01162 13.4934 7.61816 12.9492C7.40795 12.6583 7.47282 12.2523 7.76367 12.042C8.05441 11.8318 8.46045 11.897 8.6709 12.1875C8.90133 12.5062 9.38339 12.7753 9.99902 12.7754H10.1553C11.0354 12.7754 11.4902 12.2271 11.4902 11.8369C11.4901 11.5637 11.2909 11.2162 10.8018 11.0205L8.71387 10.1855C7.85826 9.84328 7.20801 9.09087 7.20801 8.16113V8.0957C7.20821 6.91237 8.2302 6.1139 9.34961 5.9541V5.28906C9.34961 4.93019 9.64019 4.63885 9.99902 4.63867C10.358 4.63867 10.6494 4.93008 10.6494 5.28906V5.98828C11.3604 6.13104 11.9874 6.5048 12.3809 7.04883C12.5912 7.33971 12.5262 7.74667 12.2354 7.95703C11.9444 8.16738 11.5375 8.10145 11.3271 7.81055C11.0966 7.49192 10.6147 7.22363 9.99902 7.22363H9.76172C8.92698 7.22363 8.50809 7.74162 8.50781 8.0957V8.16113C8.50781 8.43436 8.70792 8.78278 9.19727 8.97852L11.2852 9.81348C12.1405 10.1558 12.7908 10.9075 12.791 11.8369C12.791 13.0289 11.7802 13.847 10.6494 14.0342V14.71C10.6494 15.0689 10.358 15.3604 9.99902 15.3604C9.64019 15.3602 9.34961 15.0688 9.34961 14.71ZM18.3574 9.99902C18.3574 14.6152 14.6152 18.3574 9.99902 18.3574C5.38299 18.3572 1.64062 14.6151 1.64062 9.99902C1.6408 5.3831 5.3831 1.6408 9.99902 1.64062C14.6151 1.64062 18.3572 5.38299 18.3574 9.99902Z'
    ]
  },

  // Organizations - person with network
  organizations: {
    viewBox: '0 0 20 20',
    strokeWidth: 1.5,
    paths: [
      'M10.0007 9.99935V12.4993M7.91732 14.5827H5.83398M12.084 14.5827H14.1673M13.959 6.24935C13.959 8.43548 12.1868 10.2077 10.0007 10.2077C7.81452 10.2077 6.04232 8.43548 6.04232 6.24935C6.04232 4.06322 7.81452 2.29102 10.0007 2.29102C12.1868 2.29102 13.959 4.06322 13.959 6.24935ZM6.04232 14.5827C6.04232 15.8483 5.0163 16.8743 3.75065 16.8743C2.485 16.8743 1.45898 15.8483 1.45898 14.5827C1.45898 13.317 2.485 12.291 3.75065 12.291C5.0163 12.291 6.04232 13.317 6.04232 14.5827ZM12.2923 14.5827C12.2923 15.8483 11.2663 16.8743 10.0007 16.8743C8.735 16.8743 7.70898 15.8483 7.70898 14.5827C7.70898 13.317 8.735 12.291 10.0007 12.291C11.2663 12.291 12.2923 13.317 12.2923 14.5827ZM18.5423 14.5827C18.5423 15.8483 17.5163 16.8743 16.2507 16.8743C14.985 16.8743 13.959 15.8483 13.959 14.5827C13.959 13.317 14.985 12.291 16.2507 12.291C17.5163 12.291 18.5423 13.317 18.5423 14.5827Z'
    ]
  },

  // Journals - open book
  journals: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M17.8926 4.79199C17.8924 4.69089 17.8101 4.60938 17.709 4.60938H12.501C11.4792 4.60938 10.6504 5.43726 10.6504 6.45898V16.0127C10.6946 15.9826 10.7386 15.952 10.7842 15.9238C11.3827 15.5539 12.0606 15.3926 12.7314 15.3926H17.709C17.8102 15.3926 17.8926 15.3102 17.8926 15.209V4.79199ZM2.10938 15.209C2.10938 15.3101 2.19089 15.3924 2.29199 15.3926H7.26953C7.94032 15.3926 8.61834 15.554 9.2168 15.9238C9.26237 15.952 9.3064 15.9827 9.35059 16.0127V6.45898C9.35059 5.43737 8.52255 4.60955 7.50098 4.60938H2.29199C2.191 4.60955 2.10955 4.691 2.10938 4.79199V15.209ZM19.1924 15.209C19.1924 16.0282 18.5282 16.6924 17.709 16.6924H12.7314C12.2683 16.6924 11.8339 16.804 11.4678 17.0303C11.0883 17.2649 10.7816 17.6009 10.582 18L10.5352 18.0781C10.415 18.2522 10.2163 18.3593 10.001 18.3594C9.75482 18.3594 9.52907 18.2201 9.41895 18C9.21936 17.6008 8.91283 17.2649 8.5332 17.0303C8.16704 16.804 7.73272 16.6924 7.26953 16.6924H2.29199C1.47292 16.6922 0.808594 16.0281 0.808594 15.209V4.79199C0.80877 3.97303 1.47303 3.30877 2.29199 3.30859H7.50098C8.52083 3.3087 9.42438 3.7962 10 4.54785C10.5757 3.79576 11.4807 3.30859 12.501 3.30859H17.709C18.5281 3.30859 19.1922 3.97292 19.1924 4.79199V15.209Z'
    ]
  },

  // Discover Patents - checklist with magnifying glass
  discoverPatents: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M12.1475 12.1416C13.3776 10.9115 15.3724 10.9115 16.6025 12.1416C17.6749 13.214 17.8111 14.8666 17.0137 16.0879L18.168 17.2422C18.4218 17.496 18.4218 17.9083 18.168 18.1621C17.9142 18.4157 17.5028 18.4156 17.249 18.1621L16.0947 17.0078C14.8734 17.8055 13.22 17.6691 12.1475 16.5967C10.9174 15.3666 10.9174 13.3718 12.1475 12.1416ZM15.6836 13.0605C14.9611 12.3381 13.7889 12.3381 13.0664 13.0605C12.3442 13.7829 12.3443 14.9543 13.0664 15.6768C13.7889 16.3992 14.9611 16.3992 15.6836 15.6768C16.4057 14.9543 16.4058 13.7829 15.6836 13.0605ZM8.2627 11.0713C8.47809 10.7841 8.88566 10.726 9.17285 10.9414C9.46002 11.1568 9.51812 11.5644 9.30273 11.8516L7.74316 13.9307C7.53618 14.2066 7.14934 14.2734 6.8623 14.082L5.9248 13.457C5.62628 13.2579 5.54623 12.8543 5.74512 12.5557C5.94425 12.257 6.34779 12.1759 6.64648 12.375L7.07129 12.6582L8.2627 11.0713ZM16.2246 9.375V3.95801C16.2244 3.85701 16.143 3.77557 16.042 3.77539H3.95801C3.85701 3.77557 3.77557 3.85701 3.77539 3.95801V16.042C3.77557 16.143 3.85701 16.2244 3.95801 16.2246H9.375C9.73399 16.2246 10.0254 16.516 10.0254 16.875C10.0254 17.234 9.73398 17.5254 9.375 17.5254H3.95801C3.13904 17.5252 2.47479 16.861 2.47461 16.042V3.95801C2.47479 3.13904 3.13904 2.47479 3.95801 2.47461H16.042C16.861 2.47478 17.5252 3.13904 17.5254 3.95801V9.375C17.5254 9.73399 17.234 10.0254 16.875 10.0254C16.516 10.0254 16.2246 9.73398 16.2246 9.375ZM8.2627 6.07031C8.47809 5.78312 8.88566 5.72504 9.17285 5.94043C9.46003 6.15582 9.51812 6.5634 9.30273 6.85059L7.74316 8.92969C7.53618 9.20567 7.14934 9.27241 6.8623 9.08105L5.9248 8.45605C5.62629 8.25695 5.54621 7.85332 5.74512 7.55469C5.94425 7.25599 6.34779 7.17489 6.64648 7.37402L7.07129 7.65723L8.2627 6.07031ZM13.5898 6.84961C13.9488 6.84961 14.2402 7.14102 14.2402 7.5C14.2402 7.85899 13.9488 8.15039 13.5898 8.15039H11.5068C11.1479 8.15039 10.8564 7.85899 10.8564 7.5C10.8565 7.14102 11.1479 6.84961 11.5068 6.84961H13.5898Z'
    ]
  },

  // Staying Current - calendar with clock
  stayingCurrent: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M12.2441 9.52509C14.8079 8.46309 17.7457 9.68143 18.8076 12.2448C19.8696 14.8085 18.6522 17.7464 16.0889 18.8083C13.5251 19.8703 10.5873 18.6519 9.52539 16.0886C8.46424 13.5258 9.68058 10.587 12.2441 9.52509ZM17.6074 12.7419C16.8202 10.8416 14.6423 9.9394 12.7422 10.7263C10.8419 11.5134 9.9391 13.692 10.7256 15.5915C11.5128 17.4918 13.6907 18.394 15.5908 17.6071C17.4913 16.8199 18.3945 14.6419 17.6074 12.7419ZM13.5166 12.2917C13.5166 11.9327 13.808 11.6413 14.167 11.6413C14.5258 11.6415 14.8164 11.9328 14.8164 12.2917V13.8972L16.085 15.1657C16.3384 15.4195 16.3385 15.8309 16.085 16.0847C15.8311 16.3385 15.4189 16.3385 15.165 16.0847L13.707 14.6257L13.626 14.527C13.5553 14.4211 13.5167 14.2959 13.5166 14.1667V12.2917ZM16.2246 7.29169V4.60809H3.77539V16.2253H7.29199C7.65072 16.2255 7.94123 16.516 7.94141 16.8747C7.94141 17.2336 7.65083 17.5249 7.29199 17.5251H3.125C2.76601 17.5251 2.47461 17.2337 2.47461 16.8747V3.95868C2.47461 3.59969 2.76601 3.30829 3.125 3.30829H5.80859V2.29169C5.80859 1.93281 6.09917 1.64147 6.45801 1.6413C6.81699 1.6413 7.1084 1.9327 7.1084 2.29169V3.30829H12.8916V2.29169C12.8916 1.9327 13.183 1.6413 13.542 1.6413C13.9008 1.64147 14.1914 1.93281 14.1914 2.29169V3.30829H16.875C17.234 3.30829 17.5254 3.59969 17.5254 3.95868V7.29169C17.5254 7.65067 17.234 7.94208 16.875 7.94208C16.516 7.94208 16.2246 7.65067 16.2246 7.29169Z'
    ]
  },

  // Research Discovery - magnifying glass
  researchDiscovery: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M14.5586 9.16699C14.5586 6.18937 12.1446 3.77557 9.16699 3.77539C6.18926 3.77539 3.77539 6.18926 3.77539 9.16699C3.77557 12.1446 6.18937 14.5586 9.16699 14.5586C10.6559 14.5585 12.0032 13.9558 12.9795 12.9795C13.9558 12.0032 14.5585 10.6559 14.5586 9.16699ZM15.8584 9.16699C15.8583 10.7804 15.2858 12.2607 14.335 13.416L17.126 16.207C17.3798 16.4609 17.3798 16.8721 17.126 17.126C16.8721 17.3798 16.4609 17.3798 16.207 17.126L13.416 14.335C12.2607 15.2858 10.7804 15.8583 9.16699 15.8584C5.4714 15.8584 2.47479 12.8625 2.47461 9.16699C2.47461 5.47129 5.47129 2.47461 9.16699 2.47461C12.8625 2.47479 15.8584 5.4714 15.8584 9.16699Z'
    ]
  },

  // Fact-checking - bookmark with checkmark
  factChecking: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M16.0414 1.6413C16.4004 1.6413 16.6918 1.9327 16.6918 2.29169V17.4997C16.6918 17.7295 16.5702 17.9423 16.3724 18.0593C16.1991 18.1616 15.9896 18.1774 15.8051 18.1052L15.7279 18.069L9.99939 14.9089L4.27283 18.069L4.1947 18.1052C4.01026 18.1772 3.80151 18.1615 3.6283 18.0593C3.43026 17.9424 3.30798 17.7297 3.30798 17.4997V2.29169C3.30798 1.9327 3.59939 1.6413 3.95837 1.6413H16.0414ZM4.60876 16.3981L9.68591 13.5974L9.99939 14.1667H10.0004L9.68591 13.5974C9.88128 13.4896 10.1184 13.4897 10.3138 13.5974L15.392 16.3991V2.94208H4.60876V16.3981ZM11.9154 6.33173C12.1691 6.07816 12.5805 6.07827 12.8344 6.33173C13.0882 6.58557 13.0882 6.99781 12.8344 7.25165L9.62634 10.4597C9.50444 10.5816 9.33877 10.6501 9.16638 10.6501C9.03717 10.65 8.91197 10.6114 8.80603 10.5407L8.7074 10.4597L7.3324 9.08466C7.07856 8.83081 7.07856 8.41955 7.3324 8.16571C7.58624 7.91187 7.9975 7.91187 8.25134 8.16571L9.16638 9.08075L11.9154 6.33173Z'
    ]
  },

  // Literature Reviews - book with bookmark and text lines
  literatureReviews: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M16.0414 1.6413C16.4004 1.6413 16.6918 1.9327 16.6918 2.29169V17.7087C16.6916 18.0675 16.4003 18.3581 16.0414 18.3581H5.62537C4.34591 18.3581 3.30798 17.3211 3.30798 16.0417V3.95868C3.30798 2.67922 4.34591 1.6413 5.62537 1.6413H16.0414ZM5.83337 14.6081C5.15683 14.6081 4.60876 15.1571 4.60876 15.8337L4.61462 15.9587C4.67739 16.5764 5.19913 17.0583 5.83337 17.0583H15.392V14.6081H5.83337ZM10.2084 8.5163C10.5674 8.5163 10.8588 8.8077 10.8588 9.16669C10.8588 9.52567 10.5674 9.81708 10.2084 9.81708H7.29138C6.93255 9.8169 6.64197 9.52556 6.64197 9.16669C6.64197 8.80781 6.93255 8.51647 7.29138 8.5163H10.2084ZM12.7084 5.18329C13.0674 5.18329 13.3588 5.47469 13.3588 5.83368C13.3586 6.19251 13.0673 6.48309 12.7084 6.48309H7.29138C6.93266 6.48292 6.64214 6.19241 6.64197 5.83368C6.64197 5.4748 6.93255 5.18346 7.29138 5.18329H12.7084ZM4.60876 13.6257C4.97157 13.424 5.38884 13.3083 5.83337 13.3083H15.392V2.94208H5.62537C5.06388 2.94208 4.60876 3.39719 4.60876 3.95868V13.6257Z'
    ]
  },

  // Patents - document with text and image area
  patents: {
    viewBox: '0 0 20 20',
    filled: true,
    paths: [
      'M17.0576 4.79199C17.0574 4.69089 16.9752 4.60938 16.874 4.60938H3.12402C3.02303 4.60955 2.94158 4.691 2.94141 4.79199V15.209C2.94141 15.3101 3.02292 15.3924 3.12402 15.3926H16.874C16.9753 15.3926 17.0576 15.3102 17.0576 15.209V4.79199ZM14.374 7.47559C14.733 7.47559 15.0244 7.76699 15.0244 8.12598V11.042C15.0244 11.401 14.733 11.6924 14.374 11.6924H12.291C11.932 11.6924 11.6406 11.401 11.6406 11.042V8.12598C11.6406 7.76699 11.932 7.47559 12.291 7.47559H14.374ZM7.72461 9.97559C8.08348 9.97573 8.37402 10.2671 8.37402 10.626C8.37385 10.9847 8.08337 11.2752 7.72461 11.2754H5.62402C5.2653 11.2752 4.97479 10.9847 4.97461 10.626C4.97461 10.2671 5.26519 9.97576 5.62402 9.97559H7.72461ZM12.9414 10.3926H13.7246V8.77539H12.9414V10.3926ZM9.37402 7.47559C9.73301 7.47559 10.0244 7.76699 10.0244 8.12598C10.0242 8.48481 9.7329 8.77539 9.37402 8.77539H5.62402C5.2653 8.77521 4.97479 8.4847 4.97461 8.12598C4.97461 7.7671 5.26519 7.47576 5.62402 7.47559H9.37402ZM18.3574 15.209C18.3574 16.0282 17.6932 16.6924 16.874 16.6924H3.12402C2.30495 16.6922 1.64063 16.0281 1.64062 15.209V4.79199C1.6408 3.97303 2.30506 3.30877 3.12402 3.30859H16.874C17.6931 3.30859 18.3572 3.97292 18.3574 4.79199V15.209Z'
    ]
  }
}

/**
 * Icon component - renders inline SVG icons
 */
const Icon = ({ name, size = 24, color = 'currentColor', className, strokeWidth = 2, ...rest }) => {
  const icon = ICONS[name]

  if (!icon) {
    console.warn(`Icon "${name}" not found. Available icons: ${Object.keys(ICONS).join(', ')}`)
    return null
  }

  const { viewBox = '0 0 24 24', paths, type, circles, filled = false, strokeWidth: iconStrokeWidth } = icon

  // Use icon-specific strokeWidth if defined, otherwise use prop or default
  const finalStrokeWidth = iconStrokeWidth ?? strokeWidth

  // Handle circle-based icons (like moreVertical)
  if (type === 'circles' && circles) {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width={size}
        height={size}
        viewBox={viewBox}
        fill={color}
        className={classNames(styles.icon, className)}
        aria-hidden='true'
        {...rest}
      >
        {circles.map((circle, index) => (
          <circle key={index} cx={circle.cx} cy={circle.cy} r={circle.r} />
        ))}
      </svg>
    )
  }

  // Handle path-based icons
  // Filled icons (from Figma) use fill, stroke-based icons use stroke
  const svgProps = filled
    ? {
        fill: color,
        stroke: 'none'
      }
    : {
        fill: 'none',
        stroke: color,
        strokeWidth: finalStrokeWidth,
        strokeLinecap: 'round',
        strokeLinejoin: 'round'
      }

  // Add fillRule for filled icons with complex paths (e.g., from Figma outline strokes)
  const pathProps = filled ? { fillRule: 'evenodd', clipRule: 'evenodd' } : {}

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={size}
      height={size}
      viewBox={viewBox}
      className={classNames(styles.icon, className)}
      aria-hidden='true'
      {...svgProps}
      {...rest}
    >
      {paths.map((d, index) => (
        <path key={index} d={d} {...pathProps} />
      ))}
      {/* Render filled paths (for mixed stroke/fill icons like chat) */}
      {icon.filledPaths &&
        icon.filledPaths.map((d, index) => <path key={`filled-${index}`} d={d} fill={color} stroke='none' />)}
    </svg>
  )
}

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
  strokeWidth: PropTypes.number
}

// ============================================
// NAMED EXPORTS - for convenient imports
// ============================================

// Chat & Messaging
export const ChatIcon = props => <Icon name='chat' {...props} />
export const NewChatIcon = props => <Icon name='newChat' {...props} />

// Table & Data
export const TableIcon = props => <Icon name='table' {...props} />

// Actions
export const TrashIcon = props => <Icon name='trash' {...props} />
export const EditIcon = props => <Icon name='edit' {...props} />
export const ShareIcon = props => <Icon name='share' {...props} />
export const CopyIcon = props => <Icon name='copy' {...props} />
export const DownloadIcon = props => <Icon name='download' {...props} />
export const SearchIcon = props => <Icon name='search' {...props} />
export const PlusIcon = props => <Icon name='plus' {...props} />
export const XIcon = props => <Icon name='x' {...props} />
export const CheckIcon = props => <Icon name='check' {...props} />
export const ExternalLinkIcon = props => <Icon name='externalLink' {...props} />

// UI Elements
export const HelpIcon = props => <Icon name='help' {...props} />
export const InfoIcon = props => <Icon name='info' {...props} />
export const WarningIcon = props => <Icon name='warning' {...props} />
export const SettingsIcon = props => <Icon name='settings' {...props} />
export const SparklesIcon = props => <Icon name='sparkles' {...props} />

// Navigation
export const ChevronRightIcon = props => <Icon name='chevronRight' {...props} />
export const ChevronLeftIcon = props => <Icon name='chevronLeft' {...props} />
export const ChevronDownIcon = props => <Icon name='chevronDown' {...props} />
export const ChevronUpIcon = props => <Icon name='chevronUp' {...props} />

// Menus
export const MoreVerticalIcon = props => <Icon name='moreVertical' {...props} />
export const MoreHorizontalIcon = props => <Icon name='moreHorizontal' {...props} />

// Misc
export const BookmarkIcon = props => <Icon name='bookmark' {...props} />
export const HistoryIcon = props => <Icon name='history' {...props} />
export const FolderIcon = props => <Icon name='folder' {...props} />
export const EyeIcon = props => <Icon name='eye' {...props} />
export const EyeOffIcon = props => <Icon name='eyeOff' {...props} />
export const TagIcon = props => <Icon name='tag' {...props} />
export const BellIcon = props => <Icon name='bell' {...props} />
export const LightbulbIcon = props => <Icon name='lightbulb' {...props} />
export const SearchChecklistIcon = props => <Icon name='searchChecklist' {...props} />
export const DashboardIcon = props => <Icon name='dashboardLayout' {...props} />
export const PatentsIcon = props => <Icon name='patents' {...props} />
export const JournalsIcon = props => <Icon name='journals' {...props} />
export const OrganizationsIcon = props => <Icon name='organizations' {...props} />
export const FundersIcon = props => <Icon name='funders' {...props} />
export const BrowserExtensionIcon = props => <Icon name='browserExtension' {...props} />
export const ApiIcon = props => <Icon name='api' {...props} />
export const McpIcon = props => <Icon name='mcp' {...props} />
export const DemoIcon = props => <Icon name='demo' {...props} />

// Homepage feature cards
export const LiteratureReviewsIcon = props => <Icon name='literatureReviews' {...props} />

// Sidebar toggle (stateful)
export const SidebarToggleIcon = ({ expanded, ...props }) => (
  <Icon name={expanded ? 'sidebarExpanded' : 'sidebarCollapsed'} {...props} />
)

SidebarToggleIcon.propTypes = {
  expanded: PropTypes.bool
}

// Export icon names for reference
export const ICON_NAMES = Object.keys(ICONS)

export default Icon
